import { P2GBox } from '@parcel2go/core-ui';
import { P2GGrid } from '@parcel2go/core-ui';
import { P2GLink } from '@parcel2go/core-ui';
import { P2GText } from '@parcel2go/core-ui';
import { useMemo } from 'react';
import { SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { SumNumber, onlyUniqueNonBlank } from '../../../lib/helpers/ArrayHelper';
import { DimensionAndWeightDisplay } from '../DimensionDisplay';
import { getLocalUnitsEntry } from '~/apps/smartsend/lib/helpers/LocalUnitsEntryHelper';
import { EditType } from './EditType';
import ItemAvatar from './ItemAvatar';
import { FixedColumn } from '../../FixedColumn';
import { ToInlineCommaList } from '../../../lib/helpers/StringHelper';
import { toDisplayCurrency } from '~/components/currency/toDisplayCurrency';
import { GetSkus } from './GetSkus';
import { TranslationNamespaces } from '~/lib/constants';
import { Trans, useTranslation } from 'react-i18next';

interface props {
  errors?: string[];
  item: SmartSendItemApiModel;
  edit?: (editType: EditType) => void;
  revealText?: string;
  variant?: 'body2';
}

const GenerateItemDescription = (item: SmartSendItemApiModel) => {
  const uniqueDescriptions = item.MarketplaceOrderLines.map((x) => x.Product?.Description).filter(onlyUniqueNonBlank);
  const quantities = uniqueDescriptions.map((desc) => {
    const qty = SumNumber(
      item.MarketplaceOrderLines.filter((p) => p.Product?.Description === desc).map((p) => p.Quantity)
    );
    return qty > 1 || uniqueDescriptions.length > 1 ? `${qty}x ${desc}` : desc;
  });
  return ToInlineCommaList(quantities) || item.Description;
};

export default function ItemContents(props: props) {
  const edit = () => props.edit && props.edit('CONTENTS');
  const desc = useMemo(() => GenerateItemDescription(props.item), [props.item]);
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  return (
    <P2GBox p={1}>
      <FixedColumn
        spacing={2}
        left={props.item.Type === 'SmartSend' ? <ItemAvatar item={props.item} /> : null}
        width={85}
        right={
          <>
            <P2GBox>
              <P2GText variant={props.variant}>
                {desc.length < 80 ? (
                  <strong>{desc}</strong>
                ) : (
                  <Trans
                    t={t}
                    i18nKey="ITEM_QUANTITY"
                    values={{ quantity: SumNumber(props.item.MarketplaceOrderLines.map((x) => x.Quantity)) }}
                    defaults="<strong>Parcel Contents</strong> (Qty: {{quantity}})"
                  />
                )}
                {props.edit && (
                  <P2GLink variant="body2" ml={1} onClick={edit}>
                    {props.revealText || t('EDIT_LINK', 'Edit')}
                  </P2GLink>
                )}
              </P2GText>
            </P2GBox>
            <P2GBox mt={1}>
              <DimensionAndWeightDisplay
                dimensions={props.item.PackageDimensions}
                localUnitsEntry={getLocalUnitsEntry(props.item.Meta)}
                variant={props.variant}
              />
            </P2GBox>
            {props.item.Value > 0 && (
              <P2GBox>
                <P2GText variant={props.variant}>
                  {t('ITEM_VALUE', {
                    defaultValue: 'Value: {{amount}}',
                    amount: toDisplayCurrency(props.item.CurrencyCode, props.item.Value)
                  })}
                </P2GText>
              </P2GBox>
            )}
          </>
        }
      />
    </P2GBox>
  );
}

export const ItemContentsMobile: React.FC<props> = (props) => {
  const edit = () => props.edit && props.edit('CONTENTS');
  const desc = useMemo(() => GenerateItemDescription(props.item), [props.item]);
  const skus = GetSkus(props.item).join(', ');
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  return (
    <P2GGrid container gap={1} alignContent="center" wrap="nowrap">
      <P2GGrid item flexGrow={1}>
        <FixedColumn
          spacing={1}
          alignItems="center"
          left={<ItemAvatar item={props.item} size={35} />}
          width={40}
          right={
            <P2GBox>
              <P2GLink variant={props.variant} onClick={edit}>
                {desc.length < 200 ? (
                  <strong>{desc}</strong>
                ) : (
                  <>
                    <Trans
                      t={t}
                      i18nKey="ITEM_QUANTITY"
                      values={{ qantity: SumNumber(props.item.MarketplaceOrderLines.map((x) => x.Quantity)) }}
                      defaults="<strong>Parcel Contents</strong> (Qty: {{qantity}})"
                    />
                  </>
                )}
              </P2GLink>
              {skus && (
                <P2GText variant="caption" component="div">
                  {skus}
                </P2GText>
              )}
            </P2GBox>
          }
        />
      </P2GGrid>
      {props.edit && (
        <P2GGrid item flexShrink={1}>
          <P2GLink variant={props.variant} onClick={edit}>
            {props.errors?.length ? t('FIX_LINK', 'Fix') : props.revealText || t('VIEW_LINK', 'View')}
          </P2GLink>
        </P2GGrid>
      )}
    </P2GGrid>
  );
};
